import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface StockValueItem {
  stock_id: string;
  stock_name: string | null;
  product_id: string;
  product_name: string | null;
  quantity: string;
  weight: string;
  price: string | null;
  value: string | null;
  location_id: string | null;
  location_name: string | null;
  lot_id: string | null;
  lot_number: string | null;
}

export interface StockValueReportResponse {
  items: StockValueItem[];
  total_value: string;
  total_quantity: string;
  total_weight: string;
  as_of: string;
}

export interface StockQuantityResponse {
  stock_id: string;
  quantity: string;
  weight: string;
  unit: number;
}

export interface TrajectoryItem {
  date: string;
  type: string;
  description: string;
  quantity_change: string;
  weight_change: string;
  balance_quantity: string;
  balance_weight: string;
  reference_id: string;
  reference_type: string;
  // Operation info (if flow is part of an operation)
  operation_id: string | null;
  operation_reference: string | null;
  operation_type: string | null;
}

export interface PendingMovement {
  id: string;
  type: string;
  direction: string;
  quantity: string;
  weight: string;
  expected_date: string | null;
  description: string | null;
}

export interface SnapshotResponse {
  id: string;
  stock_id: string;
  quantity: string;
  weight: string;
  snapshot_at: string;
}

export interface FlowChainResponse {
  flow_id: string;
  flows: string[];
  total_purchase_cost: string;
  total_fees: string;
  total_sale_revenue: string;
  gross_return: string;
  return_percentage: string | null;
}

export interface StockValueFilters {
  as_of?: string;
  product_id?: UUID;
  location_id?: UUID;
  lot_id?: UUID;
  include_empty?: boolean;
}

export interface ReturnAnalysisFilters {
  start_date?: string;
  end_date?: string;
  product_id?: UUID;
  lot_id?: UUID;
}

export interface ReturnAnalysisItem {
  lot_id: string | null;
  lot_number: string | null;
  product_id: string;
  product_name: string;
  purchase_cost: string;
  fees: string;
  sale_revenue: string;
  gross_return: string;
  return_percentage: string | null;
}

export interface ReturnAnalysisResponse {
  items: ReturnAnalysisItem[];
  total_purchase_cost: string;
  total_fees: string;
  total_revenue: string;
  total_gross_return: string;
  overall_return_percentage: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface BatchQuantityFilters {
  stock_ids?: UUID[];
  product_id?: UUID;
  location_id?: UUID;
  lot_id?: UUID;
  include_empty?: boolean;
}

export interface BatchStockQuantityItem {
  stock_id: string;
  product_id: string;
  product_name: string | null;
  location_id: string | null;
  location_name: string | null;
  lot_id: string | null;
  lot_number: string | null;
  real_quantity: string;
  real_weight: string;
  provisional_quantity: string;
  provisional_weight: string;
  pending_inbound: string;
  pending_outbound: string;
}

export interface BatchStockQuantityResponse {
  items: BatchStockQuantityItem[];
  total_real_quantity: string;
  total_real_weight: string;
  total_provisional_quantity: string;
  total_provisional_weight: string;
}

export interface InventoryTurnoverFilters {
  start_date: string;
  end_date: string;
  product_id?: UUID;
}

export interface InventoryTurnoverItem {
  product_id: string;
  product_name: string | null;
  average_inventory_weight: string;
  cost_of_goods_sold: string;
  turnover_ratio: string | null;
  days_to_sell: string | null;
}

export interface InventoryTurnoverResponse {
  items: InventoryTurnoverItem[];
  total_average_inventory: string;
  total_cogs: string;
  overall_turnover_ratio: string | null;
  overall_days_to_sell: string | null;
  start_date: string;
  end_date: string;
}

export default class APIReports extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  getStockValueReport = async (
    filters?: StockValueFilters,
  ): Promise<StockValueReportResponse> => {
    const params = new URLSearchParams();
    if (filters?.as_of) params.append("as_of", filters.as_of);
    if (filters?.product_id) params.append("product_id", filters.product_id);
    if (filters?.location_id) params.append("location_id", filters.location_id);
    if (filters?.lot_id) params.append("lot_id", filters.lot_id);
    if (filters?.include_empty !== undefined)
      params.append("include_empty", String(filters.include_empty));

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/reports/stock-value${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getStockQuantity = async (stockId: UUID): Promise<StockQuantityResponse> => {
    const res = await this._fetch({
      url: `/reports/stock/${stockId}/quantity`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getStockProvisionalQuantity = async (
    stockId: UUID,
  ): Promise<StockQuantityResponse> => {
    const res = await this._fetch({
      url: `/reports/stock/${stockId}/provisional`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getStockAtDate = async (
    stockId: UUID,
    targetDate: string,
  ): Promise<StockQuantityResponse & { as_of: string }> => {
    const params = new URLSearchParams();
    params.append("target_date", targetDate);

    const res = await this._fetch({
      url: `/reports/stock/${stockId}/at-date?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getStockTrajectory = async (stockId: UUID): Promise<TrajectoryItem[]> => {
    const res = await this._fetch({
      url: `/reports/stock/${stockId}/trajectory`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getStockPendingMovements = async (
    stockId: UUID,
  ): Promise<PendingMovement[]> => {
    const res = await this._fetch({
      url: `/reports/stock/${stockId}/pending`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  createCompanySnapshots = async (): Promise<{ created_count: number }> => {
    const res = await this._fetch({
      url: `/reports/snapshots`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  createStockSnapshot = async (stockId: UUID): Promise<SnapshotResponse> => {
    const res = await this._fetch({
      url: `/reports/stock/${stockId}/snapshot`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getFlowChain = async (flowId: UUID): Promise<FlowChainResponse> => {
    const res = await this._fetch({
      url: `/reports/flow/${flowId}/chain`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getReturnAnalysis = async (
    filters?: ReturnAnalysisFilters,
  ): Promise<ReturnAnalysisResponse> => {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append("start_date", filters.start_date);
    if (filters?.end_date) params.append("end_date", filters.end_date);
    if (filters?.product_id) params.append("product_id", filters.product_id);
    if (filters?.lot_id) params.append("lot_id", filters.lot_id);

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/reports/returns${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getBatchStockQuantities = async (
    filters?: BatchQuantityFilters,
  ): Promise<BatchStockQuantityResponse> => {
    const params = new URLSearchParams();
    if (filters?.stock_ids && filters.stock_ids.length > 0) {
      params.append("stock_ids", filters.stock_ids.join(","));
    }
    if (filters?.product_id) params.append("product_id", filters.product_id);
    if (filters?.location_id) params.append("location_id", filters.location_id);
    if (filters?.lot_id) params.append("lot_id", filters.lot_id);
    if (filters?.include_empty !== undefined)
      params.append("include_empty", String(filters.include_empty));

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/reports/batch-quantities${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getInventoryTurnover = async (
    filters: InventoryTurnoverFilters,
  ): Promise<InventoryTurnoverResponse> => {
    const params = new URLSearchParams();
    params.append("start_date", filters.start_date);
    params.append("end_date", filters.end_date);
    if (filters?.product_id) params.append("product_id", filters.product_id);

    const res = await this._fetch({
      url: `/reports/inventory-turnover?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
